/**
 * This module defines the Create Folder action and its bindings to an `ItemsScope`.
 */
import * as React from 'react';
import { createFolder } from '../../../../operations/createFolder';
import {
  withOnlyCurrentItem,
  IDecorateWithOnlyCurrentItemProps
} from '../../../decorators/selectors';
import { createFolderActionControl, ICreateFolderWizardProps } from '../../../../controls/createFolderActionControl';
import { WizardAction, IWizardActionStateProps } from '../../../wizardAction/WizardAction';
import { folderFacet } from '../../../../facets/ItemFacets';
import { getItemActionEngagement } from '../../../../models/telemetry/getItemEngagement';
import { connectToItemCache } from '../../../decorators/connectToItemCache';
import { IItemCache } from '../../../../models/IItemCacheObserver';
import { ISelectedActionProps, ActionSelector } from '../../../withAction/WithAction';

export interface IItemCreateFolderActionProps {
  itemKey: string;
  createFolderWizard: React.ComponentType<ICreateFolderWizardProps>;
}

export const ItemCreateFolderAction = connectToItemCache((
  itemCache: IItemCache,
  props: IItemCreateFolderActionProps
): ISelectedActionProps => {
  const {
    itemKey,
    createFolderWizard
  } = props;

  if (!itemKey) {
    return {
      action: null
    };
  }

  let isAvailable = true;

  let FinalCreateFolderWizard = createFolderWizard;

  const folder = itemCache.demandItemFacet(folderFacet, itemKey);
  const controlHandler = itemCache.demandItemFacet(createFolderActionControl, itemKey);
  const {
    isAvailable: itemIsAvailable,
    createFolderWizard: CreateFolderWizard,
    validator,
    maxLength,
    title
  } = createFolderActionControl.evaluate(controlHandler)(itemCache, {
    itemKey: itemKey,
    isAvailable: false,
    createFolderWizard: createFolderWizard
  });

  if (!itemIsAvailable) {
    isAvailable = false;
  }

  FinalCreateFolderWizard = CreateFolderWizard;

  const { dispatch } = itemCache;

  const onCreateFolder = async ({ name }: { name: string; }) => {
    await dispatch(createFolder({
      items: {
        [itemKey]: {
          folders: {
            [name]: {}
          }
        }
      }
    }));
  };

  return {
    action: (
      <WizardAction
        isAvailable={ isAvailable }
        { ...getItemActionEngagement(itemCache, {
          itemKey,
          engagement: {
            name: 'CreateFolder',
            isIntentional: true
          }
        }) }
      >
        { (wizardProps: IWizardActionStateProps) => {
          return (
            <FinalCreateFolderWizard
              { ...wizardProps }
              isAvailable={ isAvailable && !!folder }
              itemKey={ itemKey }
              maxLength={ maxLength }
              onCreateFolder={ onCreateFolder }
              title={ title }
              validator={ validator }
            />
          );
        } }
      </WizardAction>
    )
  };
})(ActionSelector);

export type ICreateFolderActionProps = IDecorateWithOnlyCurrentItemProps<IItemCreateFolderActionProps>;

export const CreateFolderAction: React.ComponentType<ICreateFolderActionProps> = withOnlyCurrentItem()(ItemCreateFolderAction);
